import { z } from "zod";

export const CREDENTIAL_RECOVERY_WINDOW_MS = 15 * 60_000;

const apiKeyRecoverySchema = z
  .object({
    id: z.string().min(1),
    kind: z.literal("api_key"),
    organizationId: z.string().min(1),
    operationId: z.string().min(8).max(128),
    userId: z.string().min(1),
    name: z.string().min(1),
    agentName: z.string().nullable(),
    recoverableUntil: z.string().datetime(),
  })
  .loose();

const guestLinkRecoverySchema = z
  .object({
    id: z.string().min(1),
    kind: z.literal("guest_link"),
    organizationId: z.string().min(1),
    operationId: z.string().min(8).max(128),
    userId: z.string().min(1),
    siteId: z.string().min(1),
    siteName: z.string().min(1),
    expiresAt: z.string().datetime().nullable().default(null),
    /** The issued link's id, once the service has answered. Not a secret. */
    linkId: z.string().min(1).optional(),
    /**
     * When Copy put this link's share URL on the clipboard. A copied receipt
     * still offers Copy on its row until the window ends, but it no longer
     * asks to be saved.
     */
    copiedAt: z.string().datetime().optional(),
    recoverableUntil: z.string().datetime(),
  })
  .loose();

/**
 * Receipts are loose: a field this bundle does not know (written by a newer
 * one) is kept, never a reason to drop the receipt. A receipt that does not
 * parse drops alone, not with the rest of the list.
 */
const credentialRecoverySchema = z.discriminatedUnion("kind", [
  apiKeyRecoverySchema,
  guestLinkRecoverySchema,
]);

export type ApiKeyCredentialRecovery = z.infer<typeof apiKeyRecoverySchema>;
export type GuestLinkCredentialRecovery = z.infer<
  typeof guestLinkRecoverySchema
>;
export type CredentialRecovery = z.infer<typeof credentialRecoverySchema>;

/** False once a guest link's share URL was copied; the receipt then stays only for Copy. */
export function credentialRecoveryNeedsSaving(
  recovery: CredentialRecovery,
): boolean {
  return recovery.kind === "api_key" || recovery.copiedAt === undefined;
}

export interface CredentialRecoveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * v2 since guest receipts gained `linkId` and `copiedAt`. A tab still running
 * an older bundle parses v1 strictly: had it read those fields it would see no
 * receipts and, on its next save, overwrite every one. So this bundle reads v1
 * only to migrate it, once, and never writes it.
 */
const STORAGE_KEY = "appnotes:credential-recoveries:v2";
const LEGACY_STORAGE_KEY = "appnotes:credential-recoveries:v1";
const MAX_RECOVERY_RECORDS = 20;

/** Persist non-secret receipts that can resume interrupted credential issuance. */
export class CredentialRecoveryStore {
  readonly #now: () => number;
  readonly #storage: CredentialRecoveryStorage | null;
  #records: readonly CredentialRecovery[] | null = null;

  public constructor(
    storage: CredentialRecoveryStorage | null,
    now: () => number = Date.now,
  ) {
    this.#storage = storage;
    this.#now = now;
  }

  public list(
    organizationId: string,
    userId: string,
  ): readonly CredentialRecovery[] {
    const current = this.#prune(this.#load());
    return current.filter(
      (recovery) =>
        recovery.organizationId === organizationId &&
        recovery.userId === userId,
    );
  }

  public beginApiKey(input: {
    readonly agentName: string | null;
    readonly name: string;
    readonly operationId: string;
    readonly organizationId: string;
    readonly userId: string;
  }): ApiKeyCredentialRecovery {
    return this.#save({
      id: `api_key:${input.organizationId}:${input.operationId}`,
      kind: "api_key",
      organizationId: input.organizationId,
      operationId: input.operationId,
      userId: input.userId,
      name: input.name,
      agentName: input.agentName,
      recoverableUntil: new Date(
        this.#now() + CREDENTIAL_RECOVERY_WINDOW_MS,
      ).toISOString(),
    });
  }

  public beginGuestLink(input: {
    readonly expiresAt?: string | null;
    readonly operationId: string;
    readonly organizationId: string;
    readonly siteId: string;
    readonly siteName: string;
    readonly userId: string;
  }): GuestLinkCredentialRecovery {
    return this.#save({
      id: `guest_link:${input.siteId}:${input.operationId}`,
      kind: "guest_link",
      organizationId: input.organizationId,
      operationId: input.operationId,
      userId: input.userId,
      siteId: input.siteId,
      siteName: input.siteName,
      expiresAt: input.expiresAt ?? null,
      recoverableUntil: new Date(
        this.#now() + CREDENTIAL_RECOVERY_WINDOW_MS,
      ).toISOString(),
    });
  }

  public setDeadline<T extends CredentialRecovery>(
    recovery: T,
    recoverableUntil: string,
  ): T {
    return this.#save({ ...recovery, recoverableUntil });
  }

  /**
   * Record what the service answered for a guest link: its recovery deadline
   * and the id of the link it issued, so Access can offer Copy on that row
   * while the share URL is still recoverable.
   */
  public setIssuedGuestLink(
    recovery: GuestLinkCredentialRecovery,
    issued: { readonly id: string; readonly recoverableUntil: string },
  ): GuestLinkCredentialRecovery {
    return this.#save({
      ...recovery,
      linkId: issued.id,
      recoverableUntil: issued.recoverableUntil,
    });
  }

  /** Copy put this guest link on the clipboard: it no longer needs saving. */
  public markCopied(
    recovery: GuestLinkCredentialRecovery,
  ): GuestLinkCredentialRecovery {
    return this.#save({
      ...recovery,
      copiedAt: new Date(this.#now()).toISOString(),
    });
  }

  public remove(id: string): void {
    this.#persist(this.#load().filter((recovery) => recovery.id !== id));
  }

  #save<T extends CredentialRecovery>(recovery: T): T {
    const withoutCurrent = this.#prune(this.#load()).filter(
      (current) => current.id !== recovery.id,
    );
    this.#persist([...withoutCurrent, recovery].slice(-MAX_RECOVERY_RECORDS));
    return recovery;
  }

  #load(): readonly CredentialRecovery[] {
    if (this.#records !== null) return this.#records;
    if (this.#storage === null) {
      this.#records = [];
      return this.#records;
    }
    try {
      const raw = this.#storage.getItem(STORAGE_KEY);
      if (raw !== null) {
        this.#records = parseRecoveries(raw);
        return this.#records;
      }
      const legacy = this.#storage.getItem(LEGACY_STORAGE_KEY);
      if (legacy === null) {
        this.#records = [];
        return this.#records;
      }
      this.#persist(parseRecoveries(legacy));
    } catch {
      this.#records = [];
    }
    return this.#records ?? [];
  }

  #prune(
    records: readonly CredentialRecovery[],
  ): readonly CredentialRecovery[] {
    const current = records.filter(
      (recovery) => Date.parse(recovery.recoverableUntil) > this.#now(),
    );
    if (current.length !== records.length) this.#persist(current);
    return current;
  }

  #persist(records: readonly CredentialRecovery[]): void {
    this.#records = records;
    if (this.#storage === null) return;
    try {
      this.#storage.setItem(STORAGE_KEY, JSON.stringify(records));
    } catch {
      // Browser storage can be unavailable; retain the receipt in memory.
    }
  }
}

function parseRecoveries(raw: string): readonly CredentialRecovery[] {
  const list = z.array(z.unknown()).safeParse(JSON.parse(raw));
  if (!list.success) return [];
  return list.data.flatMap((item) => {
    const parsed = credentialRecoverySchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  });
}

/** Create a recovery store without assuming that browser storage is available. */
export function createBrowserCredentialRecoveryStore(): CredentialRecoveryStore {
  try {
    return new CredentialRecoveryStore(window.localStorage);
  } catch {
    return new CredentialRecoveryStore(null);
  }
}
